import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import {
  Zap,
  Briefcase,
  Video,
  Users,
  FileText,
  TrendingUp,
  Crown,
  ChevronRight,
  AlertTriangle
} from "lucide-react";

const UsageWidget = () => {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await fetch(`${API}/subscription/my-tier`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error("Failed to fetch usage:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-obsidian-light border border-[#222] p-6 animate-pulse">
        <div className="h-4 bg-[#333] rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-8 bg-[#333] rounded"></div>
          <div className="h-8 bg-[#333] rounded"></div>
        </div>
      </div>
    );
  }

  if (!usage) return null;

  const isRecruiter = user?.role === "recruiter";
  const limits = usage.limits || {};
  const currentUsage = usage.usage || {};

  // Define usage items based on role
  const usageItems = isRecruiter ? [
    {
      label: "Active Job Posts",
      icon: Briefcase,
      current: currentUsage.max_active_jobs || 0,
      limit: limits.max_active_jobs,
      color: "text-blue-400",
      bgColor: "bg-blue-400/20"
    },
    {
      label: "AI Interviews",
      icon: Video,
      current: currentUsage.ai_interviews_per_month || 0,
      limit: limits.ai_interviews_per_month,
      color: "text-purple-400",
      bgColor: "bg-purple-400/20"
    },
    {
      label: "Candidate Comparisons",
      icon: Users,
      current: currentUsage.max_compare_candidates || 0,
      limit: limits.max_compare_candidates,
      color: "text-green-400",
      bgColor: "bg-green-400/20"
    },
    {
      label: "AI Descriptions",
      icon: FileText,
      current: currentUsage.ai_descriptions_per_month || 0,
      limit: limits.ai_descriptions_per_month,
      color: "text-orange-400",
      bgColor: "bg-orange-400/20"
    }
  ] : [
    {
      label: "AI Practice Interviews",
      icon: Video,
      current: currentUsage.ai_practice_interviews || 0,
      limit: limits.ai_practice_interviews,
      color: "text-purple-400",
      bgColor: "bg-purple-400/20"
    }
  ];

  // Filter to only show items with limits
  const limitedItems = usageItems.filter(item => item.limit && item.limit !== 0);

  // Calculate if any limit is close to being reached (>80%)
  const hasWarning = limitedItems.some(item => {
    if (item.limit < 0) return false; // Unlimited
    return (item.current / item.limit) >= 0.8;
  });

  // Check for premium features (boolean flags)
  const premiumFeatures = isRecruiter ? [
    { label: "Export Reports", enabled: limits.can_export_reports },
    { label: "Priority Matching", enabled: limits.priority_matching },
    { label: "Save Templates", enabled: limits.save_templates }
  ] : [
    { label: "Career Insights", enabled: limits.career_insights },
    { label: "Job Recommendations", enabled: limits.job_recommendations },
    { label: "Enhanced Visibility", enabled: limits.enhanced_visibility },
    { label: "Priority Ranking", enabled: limits.priority_ranking }
  ];

  const lockedFeatures = premiumFeatures.filter(f => !f.enabled);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-obsidian-light border border-[#222] p-6"
      data-testid="usage-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-crimson" />
          Usage & Limits
        </h3>
        <span className="text-xs text-gray-500 uppercase tracking-wider">
          {usage.tier_name || "Free"}
        </span>
      </div>

      {/* Warning Banner */}
      {hasWarning && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
          <span className="text-yellow-400 text-sm">You're approaching your usage limits</span>
        </div>
      )}

      {/* Usage Bars */}
      <div className="space-y-4 mb-6">
        {limitedItems.map((item, idx) => {
          const isUnlimited = item.limit < 0;
          const percentage = isUnlimited ? 0 : Math.min(100, (item.current / item.limit) * 100);
          const isNearLimit = percentage >= 80;

          return (
            <div key={idx} data-testid={`usage-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-gray-400 text-sm flex items-center gap-2">
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  {item.label}
                </span>
                <span className={`text-sm font-medium ${isNearLimit ? 'text-yellow-400' : 'text-white'}`}>
                  {item.current} / {isUnlimited ? "∞" : item.limit}
                </span>
              </div>
              <div className="h-2 bg-[#222] rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${isNearLimit ? 'bg-yellow-400' : 'bg-gradient-to-r from-crimson to-red-500'}`}
                  style={{ width: isUnlimited ? '100%' : `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Locked Features */}
      {lockedFeatures.length > 0 && (
        <div className="mb-4">
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Locked Features</p>
          <div className="flex flex-wrap gap-2">
            {lockedFeatures.slice(0, 3).map((feature, idx) => (
              <span
                key={idx}
                className="text-xs px-2 py-1 bg-[#222] border border-[#333] text-gray-500"
              >
                🔒 {feature.label}
              </span>
            ))}
            {lockedFeatures.length > 3 && (
              <span className="text-xs px-2 py-1 text-gray-500">
                +{lockedFeatures.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Upgrade CTA */}
      {(hasWarning || lockedFeatures.length > 0) && (
        <Link to="/settings" data-testid="upgrade-cta">
          <Button className="w-full bg-gradient-to-r from-crimson to-red-600 hover:from-crimson-hover hover:to-red-700 text-white">
            <Crown className="w-4 h-4 mr-2" />
            Upgrade Plan
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Button>
        </Link>
      )}

      {/* View Details Link */}
      {!hasWarning && lockedFeatures.length === 0 && (
        <Link
          to="/settings"
          className="text-sm text-gray-500 hover:text-crimson transition-colors flex items-center gap-1"
        >
          View subscription details <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </motion.div>
  );
};

export default UsageWidget;
